// İlaç alarm planı editörü — düz editör dili (kutusuz bölümler, saat ekleme
// drawer'la). Kurallar: docs/design-principles.md §8.3

import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import {
  AccessTime as ClockIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { formatDistanceToNowStrict } from "date-fns";
import { tr } from "date-fns/locale";

import { FormTextField, FormSelect, FormDatePicker, ChoiceChipGroup, ActionButton } from "../../atoms";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import {
  emptyForm,
  planToForm,
  validateForm,
  DOSE_UNIT_OPTIONS,
  INSTRUCTION_OPTIONS,
  DAY_OPTIONS,
} from "./medicationPlanForm";
import {
  EDITOR_PX,
  SECTION_SX,
  ITEM_ROW_SX,
  ITEM_NAME_SX,
  listAreaSx,
  FieldRow,
  SectionHeader,
  SectionEmpty,
  EditorHeader,
} from "./_editorParts";
import { ReadOnlyStrip } from "./_workspaceParts";
import DoseTimeDrawer from "./drawers/DoseTimeDrawer";
import useTerms from "../../../hooks/useTerms";

// Saat listesi sabit yükseklikte (bkz. _editorParts listAreaSx)
const TIME_LIST_HEIGHT = 196;

const dayPart = (t) => {
  const h = Number(String(t).slice(0, 2));
  if (h >= 5 && h < 11) return "Sabah";
  if (h >= 11 && h < 15) return "Öğle";
  if (h >= 15 && h < 22) return "Akşam";
  return "Gece";
};

const relative = (value) => {
  if (!value) return "";
  try {
    return formatDistanceToNowStrict(new Date(value), { addSuffix: true, locale: tr });
  } catch (_) {
    return "";
  }
};

const errorText = (msg) =>
  msg ? <Typography sx={{ color: COLORS.errorDark, fontSize: TYPO.caption, mt: 0.5 }}>{msg}</Typography> : null;

/**
 * lockedFields: ["*"] = tamamı salt-okunur; ya da tek tek alan adları
 * ("name", "dose", "schedule.times" …).
 *
 * @param {object|null} plan - null = yeni plan
 * @param {boolean} canEdit
 * @param {string[]} [lockedFields]
 * @param {(form) => void} onSave
 * @param {() => void} onCancel
 * @param {boolean} [saving]
 * @param {React.ReactNode} [actions] - Başlık satırının sağı (Duraklat, Sonlandır …)
 */
export default function MedicationPlanEditor({
  plan,
  canEdit,
  lockedFields = [],
  onSave,
  onCancel,
  saving = false,
  actions,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const [form, setForm] = useState(() => (plan ? planToForm(plan) : emptyForm()));
  const [errors, setErrors] = useState({});
  const [timeDrawer, setTimeDrawer] = useState(false);

  useEffect(() => {
    setForm(plan ? planToForm(plan) : emptyForm());
    setErrors({});
  }, [plan]);

  const allLocked = !canEdit || lockedFields.includes("*");
  const locked = (f) => allLocked || lockedFields.includes(f);
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));
  const input = (field) => ({ variant: "standard", size: "small", fullWidth: true, disabled: locked(field), palette });

  const submit = () => {
    const e = validateForm(form);
    setErrors(e);
    if (Object.keys(e).length === 0) onSave(form);
  };

  const patientShifted = useMemo(
    () => plan?.source === "clinic" && (plan.history || []).some((h) => h.byRole && h.byRole !== "clinic"),
    [plan]
  );

  const headerLabel = plan
    ? [plan.source === "patient" ? `${terms.patient} planı` : "Klinik planı", relative(plan.updatedAt) && `güncellendi ${relative(plan.updatedAt)}`]
        .filter(Boolean)
        .join(" · ")
    : "";

  const timesLocked = locked("schedule.times");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Düzenleyen kişi populate edilmez; başlıkta kaynak + zaman yazılır */}
      <EditorHeader
        record={plan ? { _id: plan._id } : null}
        newLabel="Yeni alarm planı"
        editLabel={headerLabel}
        palette={palette}
        actions={actions}
      />

      {plan?.source === "patient" && allLocked ? (
        <ReadOnlyStrip>{terms.patientGen} eklediği plan — yalnız görüntülenebilir</ReadOnlyStrip>
      ) : null}
      {patientShifted ? (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
            ...EDITOR_PX,
            py: 1,
            bgcolor: COLORS.slate50,
            borderBottom: `1px solid ${COLORS.slate100}`,
          }}
        >
          <InfoIcon sx={{ fontSize: 16, color: COLORS.slate500 }} />
          <Typography sx={{ fontSize: TYPO.label, fontWeight: 600, color: COLORS.slate600 }}>
            {terms.patient} veya yakını bu planın saatlerini değiştirdi.
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {/* İlaç */}
        <Box sx={SECTION_SX}>
          <FieldRow label="İlaç adı">
            <FormTextField
              {...input("name")}
              placeholder="Ör. Metformin 1000 mg"
              value={form.name}
              onChange={(e) => set("name")(e.target.value)}
              inputProps={{ "aria-label": "İlaç adı" }}
              error={!!errors.name}
              helperText={errors.name}
            />
          </FieldRow>
          <FieldRow label="Doz">
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormTextField
                {...input("dose")}
                type="number"
                value={form.dose}
                onChange={(e) => set("dose")(e.target.value)}
                inputProps={{ min: 0, step: 0.5, "aria-label": "Doz" }}
                error={!!errors.dose}
                helperText={errors.dose}
                sx={{ maxWidth: 96 }}
              />
              <FormSelect
                {...input("doseUnit")}
                fullWidth={false}
                value={form.doseUnit}
                onChange={set("doseUnit")}
                options={DOSE_UNIT_OPTIONS}
                placeholder={null}
                sx={{ minWidth: 140 }}
                inputProps={{ "aria-label": "Doz birimi" }}
              />
            </Box>
          </FieldRow>
          <FieldRow label="Talimat">
            <FormSelect
              {...input("instructions")}
              value={form.instructions}
              onChange={set("instructions")}
              options={INSTRUCTION_OPTIONS}
              placeholder={null}
              inputProps={{ "aria-label": "Talimat" }}
            />
          </FieldRow>
        </Box>

        {/* Doz saatleri */}
        <Box sx={SECTION_SX}>
          <SectionHeader
            title="Doz Saatleri"
            count={form.times.length}
            addLabel="Saat Ekle"
            onAdd={timesLocked ? undefined : () => setTimeDrawer(true)}
            palette={palette}
          />
          <Box sx={listAreaSx(TIME_LIST_HEIGHT, form.times.length === 0)}>
            {form.times.length === 0 ? (
              <SectionEmpty
                icon={<ClockIcon />}
                title="Henüz saat eklenmedi"
                hint={timesLocked ? undefined : "Saat Ekle ile alarm saatlerini seçin."}
              />
            ) : (
              <Box>
                {form.times.map((t) => (
                  <Box key={t} sx={ITEM_ROW_SX}>
                    <ClockIcon sx={{ fontSize: 18, color: palette.primary, flexShrink: 0 }} />
                    <Typography sx={{ ...ITEM_NAME_SX, fontVariantNumeric: "tabular-nums", minWidth: 48 }}>{t}</Typography>
                    <Typography sx={{ flex: 1, fontSize: TYPO.label, color: COLORS.slate500 }}>{dayPart(t)}</Typography>
                    {timesLocked ? null : (
                      <IconButton
                        size="small"
                        aria-label={`${t} kaldır`}
                        onClick={() => set("times")(form.times.filter((x) => x !== t))}
                        sx={{ color: COLORS.slate400, "&:hover": { color: COLORS.errorDark } }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          {errorText(errors.times)}
        </Box>

        {/* Takvim */}
        <Box sx={{ ...SECTION_SX, borderBottom: "none" }}>
          <FieldRow label="Günler">
            <ChoiceChipGroup
              multiple
              options={DAY_OPTIONS}
              value={form.days}
              onChange={set("days")}
              disabled={locked("schedule.days")}
              ariaLabel="Günler"
            />
            <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mt: 0.5 }}>
              Hiçbiri seçili değilse her gün.
            </Typography>
          </FieldRow>
          <FieldRow label="Başlangıç">
            <FormDatePicker
              value={form.startDate}
              onChange={set("startDate")}
              disabled={locked("schedule.startDate")}
              clearable={false}
              variant="standard"
              palette={palette}
            />
            {errorText(errors.startDate)}
          </FieldRow>
          <FieldRow label="Bitiş">
            <FormDatePicker
              value={form.endDate}
              onChange={set("endDate")}
              disabled={locked("schedule.endDate")}
              placeholder="Süresiz"
              variant="standard"
              palette={palette}
            />
            {errorText(errors.endDate)}
          </FieldRow>
        </Box>
      </Box>

      {!allLocked ? (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            ...EDITOR_PX,
            py: 1.5,
            borderTop: `1px solid ${COLORS.slate100}`,
          }}
        >
          <ActionButton variant="edit" tone="outline" label="Vazgeç" onClick={onCancel} />
          <ActionButton
            variant="save"
            label={plan ? "Kaydet" : "Planı Kaydet"}
            loading={saving}
            onClick={submit}
            palette={palette}
          />
        </Box>
      ) : null}

      <DoseTimeDrawer
        open={timeDrawer}
        onClose={() => setTimeDrawer(false)}
        existing={form.times}
        onSubmit={(times) => {
          setForm((p) => ({ ...p, times: [...new Set([...p.times, ...times])].sort() }));
          setTimeDrawer(false);
        }}
      />
    </Box>
  );
}
